import {useContext, useEffect, useState} from 'react'

import cx from 'classnames'

import Context from '@s-ui/react-context'
import {useMount} from '@s-ui/react-hooks'
import {useRouter} from '@s-ui/react-router'
import Layout from '@adv-mt/mt-layout-basic'

import LinkFactory from '../../../components/LinkFactory/index.js'
import {UnsubscribeEmailsButtons} from './components/buttons.js'
import {UnsubscribeEmailsCheckboxForm} from './components/checkboxForm.js'
import {UnsubscribeEmailsHeader} from './components/header.js'
import {UnsubscribeEmailsRadioButtonsForm} from './components/radioButtonsForm.js'
import {UnsubscribeEmailsSkeleton} from './components/skeleton.js'
import {useTracking} from './useTracking.js'
import {compareConsents, PAGE_STATE} from './utils.js'

const UserUnsubscribesEmails = () => {
  const {domain, i18n} = useContext(Context)
  const config = domain.get('config')
  const {EXTERNAL_SERVICES_OFFERS, PORTAL_ANNOUNCEMENTS} = config.get('user').SUBSCRIPTIONS
  const {DEFAULT, ERROR, LOADING, SUCCESS} = PAGE_STATE

  const {location} = useRouter()

  const [initialConsents, setInitialConsents] = useState({
    [EXTERNAL_SERVICES_OFFERS]: true,
    [PORTAL_ANNOUNCEMENTS]: true
  })
  const [consents, setConsents] = useState({
    [EXTERNAL_SERVICES_OFFERS]: {value: true, hasChanged: false},
    [PORTAL_ANNOUNCEMENTS]: {value: true, hasChanged: false}
  })
  const [userData, setUserData] = useState({
    email: '',
    userId: location.query.userId || '',
    token: location.query.token || ''
  })
  const [formValueSelected, setFormValueSelected] = useState('')
  const [otherReasonTextArea, setOtherReasonTextArea] = useState('')
  const [pageState, setPageState] = useState(LOADING)

  const {pageViewed, successPageViewed, trackButtonClicked, trackRadioButtonsFormClicked} = useTracking()

  const handleClick = ({e, id}) => {
    setConsents({...consents, [id]: {value: !consents[id].value, hasChanged: true}})
  }

  const handleRadioButtons = ({e, id}) => {
    setFormValueSelected(id)
  }

  const handleOnChangeOtherReasonTextArea = e => {
    setOtherReasonTextArea(e.target.value)
  }

  const getUserSubscriptions = async ({subscriptionType, userId, token}) => {
    const [error, userSubscriptions] = await domain
      .get('get_subscriptions_user_use_case')
      .execute({subscriptionType, userId, token})

    setPageState(DEFAULT)
    if (error) {
      setPageState(ERROR)
      return
    }
    const {email, subscriptions} = userSubscriptions
    setUserData({...userData, email})
    setInitialConsents(subscriptions)

    if (Object.keys(subscriptions).length > 0)
      setConsents({
        [EXTERNAL_SERVICES_OFFERS]: {value: subscriptions[EXTERNAL_SERVICES_OFFERS], hasChanged: false},
        [PORTAL_ANNOUNCEMENTS]: {value: subscriptions[PORTAL_ANNOUNCEMENTS], hasChanged: false}
      })
  }

  const updateUserSubscriptions = async () => {
    setPageState(LOADING)
    const [error] = await domain
      .get('update_subscriptions_user_use_case')
      .execute({newSubscriptions: consents, userId: userData.userId, token: userData.token})
    if (error) {
      setPageState(ERROR)
    }
    setPageState(SUCCESS)
  }

  const saveUpdatedSubscriptionsOnClick = () => {
    trackButtonClicked(consents[PORTAL_ANNOUNCEMENTS].value, consents[EXTERNAL_SERVICES_OFFERS].value)
    trackRadioButtonsFormClicked(formValueSelected, otherReasonTextArea)
    updateUserSubscriptions()
  }

  const userUnsubscribeEmailsClassName = cx('mt-UserUnsubscribeEmails', {
    'mt-UserUnsubscribeEmails--isSuccess': pageState === SUCCESS
  })

  const isDifferentFromInitialState = compareConsents(initialConsents, consents)
  const showRadioButtonsForm =
    Object.values(consents).some(consent => consent.hasChanged && !consent.value) && isDifferentFromInitialState

  useMount(() => {
    pageViewed()
    getUserSubscriptions({subscriptionType: 'emails', userId: userData.userId, token: userData.token})
  })

  useEffect(() => {
    if (pageState === SUCCESS) {
      successPageViewed()
    }
  }, [pageState])

  return (
    <Layout.FullWidth isHeaderless linkFactory={LinkFactory} isFooterless>
      <Layout.Container isFullWidth isMarginless>
        <div className={userUnsubscribeEmailsClassName}>
          {pageState === LOADING && <UnsubscribeEmailsSkeleton />}
          {pageState !== LOADING && <UnsubscribeEmailsHeader pageState={pageState} userData={userData} />}
          {pageState === DEFAULT && (
            <>
              <div className="mt-UserUnsubscribeEmails-checkboxSection">
                <UnsubscribeEmailsCheckboxForm consents={consents} handleClick={handleClick} />
              </div>
              {showRadioButtonsForm && (
                <div className="mt-UserUnsubscribeEmails-radioButtonsForm">
                  <UnsubscribeEmailsRadioButtonsForm
                    formValueSelected={formValueSelected}
                    handleClick={handleRadioButtons}
                    handleOnChangeOtherReasonTextArea={handleOnChangeOtherReasonTextArea}
                    otherReasonTextArea={otherReasonTextArea}
                  />
                </div>
              )}
              <UnsubscribeEmailsButtons
                handleSaveNewSubscriptions={saveUpdatedSubscriptionsOnClick}
                isDisabled={!isDifferentFromInitialState}
                pageState={pageState}
              />
            </>
          )}
          {pageState === SUCCESS && (
            <>
              <span className="mt-UserUnsubscribeEmails-succesFullDescrciption">
                {i18n.t('UNSUBSCRIBE_EMAILS_PAGE.SUCCESS_CONTENT.DESCRIPTION')}
              </span>
              <UnsubscribeEmailsButtons
                handleSaveNewSubscriptions={saveUpdatedSubscriptionsOnClick}
                isDisabled={!isDifferentFromInitialState}
                pageState={pageState}
              />
            </>
          )}
        </div>
      </Layout.Container>
    </Layout.FullWidth>
  )
}

export default UserUnsubscribesEmails
